import React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { DnDNode, DnDNodeProps } from './DnDNode';
import { useProjectDnDContext } from './project-dnd.context';

export function useProjectNode(nodeProps: DnDNodeProps) {
  const { id, nodeType, data } = nodeProps;
  const { moveNode, isDroppable, canAccept, disabled } = useProjectDnDContext();

  const [{ isDragging }, drag] = useDrag(
    {
      type: nodeType,
      item: nodeProps,
      collect: monitor => ({
        isDragging: monitor.isDragging(),
        canDrag: monitor.canDrag(),
        item: monitor.getItem(),
        type: monitor.getItemType(),
      }),
      canDrag: monitor => {
        return (
          nodeType !== 'project' &&
          (nodeType !== 'grouping' || id > 0) &&
          !data?.archived &&
          !disabled
        );
      },
    },
    [nodeProps]
  );

  const [{ isOverNode, canDropToNode }, dropToNode] = useDrop(
    {
      accept: canAccept(nodeProps),
      drop: (item: DnDNodeProps, monitor) => {
        const didDrop = monitor.didDrop();
        const from = item;
        const to = nodeProps;
        const position = 'node';

        moveNode({ from, to, position });
        return;
      },
      canDrop: (item: DnDNodeProps) => {
        const from = item;
        const to = nodeProps;
        const position = 'node';

        return isDroppable({ from, to, position });
      },
      collect: monitor => {
        const isOverNode = monitor.isOver({ shallow: true });

        const item = monitor.getItem();
        let canDropToNode = false;

        if (item) {
          const from = item;
          const to = nodeProps;
          const position = 'node';
          canDropToNode = isDroppable({ from, to, position });
        }

        return {
          isOverNode,
          canDropToNode,
        };
      },
    },
    [nodeProps]
  );

  const [{ isOverNext, canDropToNext }, dropToNext] = useDrop(
    {
      accept: canAccept(nodeProps),
      drop: (item: DnDNodeProps) => {
        const from = item;
        const to = nodeProps;
        const position = 'next';

        moveNode({ from, to, position });
      },
      canDrop: (item, monitor) => {
        const from = item;
        const to = nodeProps;
        const position = 'next';

        return isDroppable({ from, to, position });
      },
      collect: monitor => {
        const isOverNext = monitor.isOver();

        const item = monitor.getItem();
        let canDropToNext = false;

        if (item) {
          const from = item;
          const to = nodeProps;
          const position = 'next';

          canDropToNext = isDroppable({ from, to, position });
        }

        return {
          isOverNext,
          canDropToNext,
        };
      },
    },
    [nodeProps]
  );

  const [{ isOverPrev, canDropToPrev }, dropToPrev] = useDrop(
    {
      accept: canAccept(nodeProps),
      drop: (item: DnDNodeProps) => {
        const from = item;
        const to = nodeProps;
        const position = 'prev';

        moveNode({ from, to, position });
      },
      canDrop: (item, monitor) => {
        const from = item;
        const to = nodeProps;
        const position = 'prev';

        return isDroppable({ from, to, position });
      },
      collect: monitor => {
        const isOverPrev = monitor.isOver();

        const item = monitor.getItem();
        let canDropToPrev = false;

        if (item) {
          const from = item;
          const to = nodeProps;
          const position = 'prev';

          canDropToPrev = isDroppable({ from, to, position });
        }

        return {
          isOverPrev,
          canDropToPrev,
        };
      },
    },
    [nodeProps]
  );

  return {
    drag,
    dropToNode,
    dropToNext,
    dropToPrev,
    isDragging,
    isOverNode,
    isOverNext,
    isOverPrev,
    canDropToNode,
    canDropToNext,
    canDropToPrev,
  };
}
